import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Truck, CalendarCheck, ShoppingBag, Euro, LogOut, Loader2, RefreshCw, ArrowLeft, FileDown, FileText, Trash2, BarChart3, Package, Plus, Save, ImagePlus, Search, Gift, Users, Link2, Eye, PenLine } from "lucide-react";
import { GoogleButton } from "@/pages/AccountPage";
import { LogoMark } from "@/components/Logo";
import { API, BRAND } from "@/constants/brand";
import { formatEUR } from "@/context/CartContext";

const api = axios.create({ baseURL: API, withCredentials: true });
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const VEHICLE_NAMES = { transit: "Ford Transit Kasten", movano: "Opel Movano", crafter: "VW Crafter" };
const CARGO_NAMES = { express: "Express-Paket", pakete: "Pakete & Kartons", paletten: "Palettenware" };

const TRANSPORT_STATUSES = ["gebucht", "bestätigt", "unterwegs", "abgeschlossen"];
const APPOINTMENT_STATUSES = ["angefragt", "bestätigt", "abgeschlossen"];
const PRODUCT_CATEGORIES = ["Geschenkkarte", "Reifen", "Pflege", "Zubehör"];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const fmtMonth = (m) => {
  try {
    return new Date(`${m}-01T00:00:00`).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  } catch {
    return m;
  }
};

const Badge = ({ children, tone = "blue" }) => {
  const tones = {
    blue: "border-cyan-400/30 bg-cyan-400/10 text-cyan-300",
    green: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
    amber: "border-amber-400/30 bg-amber-400/10 text-amber-300",
    gray: "border-white/10 bg-white/5 text-slate-400",
    red: "border-red-400/30 bg-red-400/10 text-red-300",
  };
  return (
    <span className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${tones[tone]}`}>
      {children}
    </span>
  );
};

const StatusPicker = ({ testid, current, options, onChange, busy }) => (
  <div className="flex flex-wrap gap-1">
    {options.map((s) => (
      <button
        key={s}
        data-testid={`${testid}-${s}`}
        disabled={busy || s === current}
        onClick={() => onChange(s)}
        className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold transition-colors ${
          s === current
            ? "border-cyan-400/60 bg-cyan-400/15 text-cyan-300 cursor-default"
            : "border-white/10 text-slate-500 hover:border-cyan-400/40 hover:text-cyan-300"
        } disabled:opacity-60`}
      >
        {s}
      </button>
    ))}
  </div>
);

const inputCls =
  "rounded-lg bg-[#0B0F17]/80 border border-white/10 px-3 py-2 text-xs text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50";

const WhatsAppIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className="fill-current" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const waPhone = (p) => {
  const d = (p || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("0049")) return d.slice(2);
  if (d.startsWith("49")) return d;
  if (d.startsWith("0")) return `49${d.slice(1)}`;
  return d;
};

const waCustomerMsg = (t) =>
  `Guten Tag ${t.name},\n\nhier ist M-Line Solutions. Aktueller Status Ihrer Sendung ${t.order_no || ""} (${t.pickup} → ${t.dropoff}): ${(t.status || "").toUpperCase()}.\n\nLive verfolgen: https://m-linesolutions.de/verfolgung?tn=${encodeURIComponent(t.order_no || "")}&email=${encodeURIComponent(t.email || "")}\n\nBei Fragen: ${BRAND.phone}`;

const waDriverMsg = (t, d) =>
  `Hallo ${d.name},\n\nneue Tour ${t.order_no || ""} zugeteilt:\nRoute: ${t.pickup} → ${t.dropoff}\nTermin: ${fmtDate(t.scheduled_at)}\nFahrzeug: ${VEHICLE_NAMES[t.vehicle] || t.vehicle}\nKunde: ${t.name}, Tel: ${t.phone}\n\nBitte kurz bestätigen. Danke!`;

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("transports");
  const [overview, setOverview] = useState(null);
  const [transports, setTransports] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [search, setSearch] = useState("");
  const [orders, setOrders] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [report, setReport] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [statusBusy, setStatusBusy] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: "", category: "Zubehör", desc: "", price_eur: "", stock: 10, image: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [rowEdits, setRowEdits] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [showDriverForm, setShowDriverForm] = useState(false);
  const [newDriver, setNewDriver] = useState({ name: "", phone: "", email: "", vehicle: "movano" });
  const [stats, setStats] = useState(null);

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const [ov, tr, ap, or, rep, pr, vo, dr, st] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/transports"),
        api.get("/admin/appointments"),
        api.get("/admin/orders"),
        api.get("/admin/reports/monthly"),
        api.get("/products"),
        api.get("/admin/vouchers"),
        api.get("/admin/drivers"),
        api.get("/admin/stats"),
      ]);
      setOverview(ov.data);
      setTransports(tr.data.transports);
      setAppointments(ap.data.appointments);
      setOrders(or.data.orders);
      setReport(rep.data.months);
      setProducts(pr.data.products);
      setVouchers(vo.data.vouchers);
      setDrivers(dr.data.drivers);
      setStats(st.data);
    } catch (e) {
      if (e.response?.status === 401) {
        try {
          await api.post("/auth/refresh");
          return loadData();
        } catch {
          setUser(false);
        }
      } else {
        toast.error("Daten konnten nicht geladen werden.");
      }
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        loadData();
      })
      .catch(() => setUser(false));
  }, [loadData]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success("Willkommen im Admin-Bereich!");
      loadData();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Anmeldung fehlgeschlagen.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  const changeStatus = async (kind, id, status) => {
    setStatusBusy(id);
    try {
      await api.patch(`/admin/${kind}/${id}/status`, { status });
      toast.success(`Status: ${status}${(status === "bestätigt" || status === "abgeschlossen") && kind === "transports" ? " · Kunde per E-Mail informiert (falls E-Mail vorhanden)" : ""}`);
      await loadData();
    } catch {
      toast.error("Status konnte nicht geändert werden.");
    } finally {
      setStatusBusy(null);
    }
  };

  const getPortalUrl = async (driverId) => {
    const { data } = await api.get(`/admin/drivers/${driverId}/portal-link`);
    return `${window.location.origin}${data.path}`;
  };

  const copyPortalLink = async (d) => {
    try {
      const url = await getPortalUrl(d.id);
      await navigator.clipboard.writeText(url);
      toast.success("Portal-Link kopiert — jetzt an den Fahrer senden.");
    } catch {
      toast.error("Link konnte nicht erstellt werden.");
    }
  };

  const sendPortalWhatsApp = async (d) => {
    try {
      const url = await getPortalUrl(d.id);
      const msg = `Hallo ${d.name},\n\nhier ist dein persönliches Touren-Portal von M-Line Solutions:\n${url}\n\nDort siehst du deine Touren, startest sie und meldest Zustellungen. Kein Login nötig — bitte den Link nicht weitergeben.`;
      window.open(`https://wa.me/${waPhone(d.phone)}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
    } catch {
      toast.error("Link konnte nicht erstellt werden.");
    }
  };

  const createDriver = async () => {
    if (!newDriver.name.trim() || !newDriver.phone.trim()) {
      toast.error("Name und Telefon sind Pflichtfelder.");
      return;
    }
    setStatusBusy("new-driver");
    try {
      await api.post("/admin/drivers", newDriver);
      toast.success("Fahrer angelegt.");
      setNewDriver({ name: "", phone: "", email: "", vehicle: "movano" });
      setShowDriverForm(false);
      await loadData();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Fahrer konnte nicht angelegt werden.");
    } finally {
      setStatusBusy(null);
    }
  };

  const assignDriver = async (rideId, driverId) => {
    setStatusBusy(rideId);
    try {
      const { data } = await api.patch(`/admin/transports/${rideId}/driver`, { driver_id: driverId || null });
      toast.success(
        data.driver_id
          ? `Fahrer ${data.driver_name} zugewiesen · E-Mail-Benachrichtigung (falls hinterlegt) unterwegs`
          : "Fahrer-Zuweisung entfernt."
      );
      await loadData();
    } catch {
      toast.error("Zuweisung fehlgeschlagen.");
    } finally {
      setStatusBusy(null);
    }
  };

  const redeemVoucher = async (code) => {
    setStatusBusy(code);
    try {
      await api.patch(`/admin/vouchers/${code}/redeem`);
      toast.success(`Gutschein ${code} eingelöst.`);
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Einlösen.");
    } finally {
      setStatusBusy(null);
    }
  };

  const deleteEntry = async (kind, id) => {
    setStatusBusy(id);
    try {
      await api.delete(`/admin/${kind}/${id}`);
      toast.success("Eintrag gelöscht.");
      await loadData();
    } catch {
      toast.error("Eintrag konnte nicht gelöscht werden.");
    } finally {
      setStatusBusy(null);
    }
  };

  const pickImage = (file) => {
    if (!file) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const createProduct = async () => {
    if (!newProduct.name.trim() || !newProduct.price_eur) {
      toast.error("Name und Preis sind Pflichtfelder.");
      return;
    }
    setStatusBusy("new-product");
    try {
      let imageUrl = newProduct.image.trim();
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        const up = await api.post("/admin/upload", fd);
        imageUrl = `${BACKEND}/api/files/${up.data.path}`;
      }
      await api.post("/admin/products", {
        ...newProduct,
        image: imageUrl,
        price_eur: parseFloat(newProduct.price_eur),
        stock: parseInt(newProduct.stock, 10) || 0,
      });
      toast.success("Produkt erstellt — sofort im Shop sichtbar.");
      setNewProduct({ name: "", category: "Zubehör", desc: "", price_eur: "", stock: 10, image: "" });
      setImageFile(null);
      if (imagePreview) URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
      setShowProductForm(false);
      await loadData();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Produkt konnte nicht erstellt werden.");
    } finally {
      setStatusBusy(null);
    }
  };

  const saveProduct = async (p) => {
    const edits = rowEdits[p.id] || {};
    const payload = {};
    if (edits.price_eur !== undefined && edits.price_eur !== String(p.price_eur)) payload.price_eur = parseFloat(edits.price_eur);
    if (edits.stock !== undefined && edits.stock !== String(p.stock ?? 0)) payload.stock = parseInt(edits.stock, 10);
    if (edits.tag !== undefined && edits.tag !== p.tag) payload.tag = edits.tag;
    if (Object.keys(payload).length === 0) {
      toast("Keine Änderungen.");
      return;
    }
    setStatusBusy(p.id);
    try {
      await api.patch(`/admin/products/${p.id}`, payload);
      toast.success("Produkt aktualisiert.");
      setRowEdits((prev) => ({ ...prev, [p.id]: {} }));
      await loadData();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Speichern fehlgeschlagen.");
    } finally {
      setStatusBusy(null);
    }
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center">
        <Loader2 size={32} className="text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center px-5 noise-overlay grid-lines">
        <div className="glass-panel rounded-3xl p-8 sm:p-10 max-w-sm w-full glow-border">
          <div className="flex justify-center mb-6">
            <LogoMark size={52} />
          </div>
          <h1 className="text-2xl font-bold text-slate-50 text-center">Admin-Bereich</h1>
          <p className="text-sm text-slate-400 text-center mt-2 mb-7">Nur für M-Line Solutions Mitarbeiter.</p>
          <form onSubmit={login} className="space-y-3">
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-Mail"
              className="w-full rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50"
            />
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Passwort"
              className="w-full rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50"
            />
            {error && (
              <p data-testid="admin-login-error" className="text-xs text-red-400">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-submit-button"
              type="submit"
              disabled={loggingIn}
              className="w-full rounded-xl bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] py-3 text-sm font-bold text-white flex items-center justify-center gap-2 transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
            >
              {loggingIn && <Loader2 size={15} className="animate-spin" />}
              Anmelden
            </button>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-[10px] uppercase tracking-widest text-slate-600">oder</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <GoogleButton label="Mit Google anmelden" redirectPath="/admin" />
          </form>
          <Link to="/" className="mt-5 flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-cyan-300 transition-colors">
            <ArrowLeft size={12} /> Zurück zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  const TABS = [
    { id: "transports", label: "Transporte", icon: Truck, count: overview?.transports },
    { id: "appointments", label: "Termine", icon: CalendarCheck, count: overview?.appointments },
    { id: "orders", label: "Bestellungen", icon: ShoppingBag, count: overview?.orders },
    { id: "gutscheine", label: "Gutscheine", icon: Gift, count: vouchers.filter((v) => !v.redeemed).length },
    { id: "products", label: "Produkte", icon: Package, count: overview?.products },
    { id: "fahrer", label: "Fahrer", icon: Users, count: drivers.length },
    { id: "stats", label: "Besucher", icon: Eye, count: null },
    { id: "reports", label: "Berichte", icon: BarChart3, count: null },
  ];

  const EXPORTS = {
    transports: { url: `${API}/admin/export/transports.csv`, label: "Transporte als CSV" },
    orders: { url: `${API}/admin/export/orders.csv`, label: "Bestellungen als CSV" },
  };

  const q = search.trim().toLowerCase();
  const filteredTransports = q
    ? transports.filter((t) => [t.order_no, t.invoice_number, t.name, t.phone, t.pickup, t.dropoff].filter(Boolean).join(" ").toLowerCase().includes(q))
    : transports;
  const filteredAppointments = q
    ? appointments.filter((a) => [a.appt_no, a.name, a.phone, a.service, a.tire_size, a.date].filter(Boolean).join(" ").toLowerCase().includes(q))
    : appointments;
  const filteredOrders = q
    ? orders.filter((o) => [o.order_ref, o.session_id, ...(o.items || [])].filter(Boolean).join(" ").toLowerCase().includes(q))
    : orders;

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-slate-950/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={34} />
            <div className="leading-none">
              <span className="font-bold text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>
                M-Line <span className="text-gradient-blue">Admin</span>
              </span>
              <span className="block text-[9px] tracking-[0.25em] text-slate-500 mt-0.5 font-mono-tech uppercase">
                {user.email}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh-button"
              onClick={loadData}
              className="rounded-full border border-white/10 p-2.5 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors"
              title="Aktualisieren"
            >
              <RefreshCw size={15} className={loadingData ? "animate-spin" : ""} />
            </button>
            <Link
              to="/"
              className="rounded-full border border-white/10 px-4 py-2 text-xs text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors"
            >
              Website
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-1.5 rounded-full border border-red-400/25 bg-red-400/5 px-4 py-2 text-xs text-red-300 hover:bg-red-400/15 transition-colors"
            >
              <LogOut size={13} /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {[
            { label: "Transporte", value: overview?.transports ?? "—", icon: Truck },
            { label: "Termine", value: overview?.appointments ?? "—", icon: CalendarCheck },
            { label: "Bestellungen", value: overview?.orders ?? "—", icon: ShoppingBag },
            { label: "Umsatz (bezahlt)", value: overview ? formatEUR(overview.revenue_cents / 100) : "—", icon: Euro },
            {
              label: "Gutscheine aktiv",
              value: formatEUR(vouchers.filter((v) => !v.redeemed).reduce((s, v) => s + (v.value_eur || 0), 0)),
              icon: Gift,
              sub: `${vouchers.filter((v) => !v.redeemed).length} gültig`,
            },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-white/8 bg-[#121826] p-5">
              <s.icon size={18} className="text-cyan-400 mb-3" />
              <div className="font-mono-tech text-2xl font-bold text-slate-50">{s.value}</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mt-1">{s.label}</div>
              {s.sub && <div className="text-[10px] text-emerald-400/80 mt-0.5 font-mono-tech">{s.sub}</div>}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-5">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => { setTab(t.id); setSearch(""); }}
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-xs font-semibold transition-colors duration-300 ${
                tab === t.id
                  ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300"
                  : "border-white/10 text-slate-400 hover:border-white/25"
              }`}
            >
              <t.icon size={14} />
              {t.label}
              {t.count != null && <span className="font-mono-tech text-[10px] opacity-70">({t.count})</span>}
            </button>
          ))}
          {(tab === "transports" || tab === "appointments" || tab === "orders") && (
            <div className="relative ml-auto">
              <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              <input
                data-testid="admin-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Suche: Nr., Name, Telefon…"
                className="w-64 rounded-full border border-white/10 bg-white/[0.03] pl-9 pr-4 py-2.5 text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none transition-colors"
              />
            </div>
          )}
          {tab === "products" && (
            <button
              data-testid="admin-new-product-button"
              onClick={() => setShowProductForm((v) => !v)}
              className="ml-auto flex items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-5 py-2.5 text-xs font-semibold text-cyan-300 hover:bg-cyan-400/20 transition-colors"
            >
              <Plus size={14} />
              Neues Produkt
            </button>
          )}
          {tab === "fahrer" && (
            <button
              data-testid="admin-new-driver-button"
              onClick={() => setShowDriverForm((v) => !v)}
              className="ml-auto flex items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-5 py-2.5 text-xs font-semibold text-cyan-300 hover:bg-cyan-400/20 transition-colors"
            >
              <Plus size={14} />
              Neuer Fahrer
            </button>
          )}
          {EXPORTS[tab] && (
            <a
              data-testid={`admin-export-${tab}`}
              href={EXPORTS[tab].url}
              download
              className="ml-auto flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/5 px-5 py-2.5 text-xs font-semibold text-emerald-300 hover:bg-emerald-400/15 transition-colors"
            >
              <FileDown size={14} />
              {EXPORTS[tab].label}
            </a>
          )}
        </div>

        {tab === "products" && showProductForm && (
          <div className="rounded-2xl border border-cyan-400/25 bg-[#121826] p-5 mb-5" data-testid="product-create-form">
            <h3 className="text-sm font-bold text-slate-100 mb-4">Neues Produkt anlegen</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <input
                data-testid="product-form-name"
                value={newProduct.name}
                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                placeholder="Produktname *"
                className={inputCls}
              />
              <select
                data-testid="product-form-category"
                value={newProduct.category}
                onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                className={`${inputCls} [color-scheme:dark]`}
              >
                {PRODUCT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <input
                data-testid="product-form-price"
                type="number"
                step="0.01"
                min="0"
                value={newProduct.price_eur}
                onChange={(e) => setNewProduct({ ...newProduct, price_eur: e.target.value })}
                placeholder="Preis in € *"
                className={inputCls}
              />
              <input
                data-testid="product-form-stock"
                type="number"
                min="0"
                value={newProduct.stock}
                onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                placeholder="Lagerbestand"
                className={inputCls}
              />
              <label
                data-testid="product-form-file"
                className="sm:col-span-2 flex items-center gap-4 rounded-lg border border-dashed border-white/15 px-4 py-2.5 cursor-pointer hover:border-cyan-400/40 transition-colors"
              >
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => pickImage(e.target.files?.[0])}
                />
                {imagePreview ? (
                  <img src={imagePreview} alt="" className="w-16 h-12 object-cover rounded-lg" />
                ) : (
                  <ImagePlus size={20} className="text-slate-500 shrink-0" />
                )}
                <span className="text-xs text-slate-400">
                  {imageFile ? imageFile.name : "Bild vom Computer hochladen (JPG, PNG, WebP · max. 5 MB)"}
                </span>
              </label>
              <textarea
                data-testid="product-form-desc"
                value={newProduct.desc}
                onChange={(e) => setNewProduct({ ...newProduct, desc: e.target.value })}
                placeholder="Kurzbeschreibung (optional)"
                rows={2}
                className={`${inputCls} sm:col-span-2 lg:col-span-3 resize-none`}
              />
              <input
                data-testid="product-form-image"
                value={newProduct.image}
                onChange={(e) => setNewProduct({ ...newProduct, image: e.target.value })}
                placeholder="…oder Bild-URL einfügen (optional)"
                className={`${inputCls} sm:col-span-2 lg:col-span-3`}
              />
            </div>
            <div className="flex gap-3 mt-4">
              <button
                data-testid="admin-product-submit-button"
                onClick={createProduct}
                disabled={statusBusy === "new-product"}
                className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] px-5 py-2.5 text-xs font-bold text-white disabled:opacity-60"
              >
                {statusBusy === "new-product" ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Produkt anlegen
              </button>
              <button
                onClick={() => setShowProductForm(false)}
                className="rounded-xl border border-white/10 px-5 py-2.5 text-xs text-slate-400 hover:border-white/25"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {tab === "fahrer" && showDriverForm && (
          <div className="rounded-2xl border border-cyan-400/25 bg-[#121826] p-5 mb-5" data-testid="driver-create-form">
            <h3 className="text-sm font-bold text-slate-100 mb-4">Neuen Fahrer anlegen</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <input
                data-testid="driver-form-name"
                value={newDriver.name}
                onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })}
                placeholder="Name *"
                className={inputCls}
              />
              <input
                data-testid="driver-form-phone"
                value={newDriver.phone}
                onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })}
                placeholder="Telefon (WhatsApp) *"
                className={inputCls}
              />
              <input
                data-testid="driver-form-email"
                type="email"
                value={newDriver.email}
                onChange={(e) => setNewDriver({ ...newDriver, email: e.target.value })}
                placeholder="E-Mail (für automatische Tour-Infos)"
                className={inputCls}
              />
              <select
                data-testid="driver-form-vehicle"
                value={newDriver.vehicle}
                onChange={(e) => setNewDriver({ ...newDriver, vehicle: e.target.value })}
                className={inputCls}
              >
                {Object.entries(VEHICLE_NAMES).map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                data-testid="driver-create-submit"
                onClick={createDriver}
                disabled={statusBusy === "new-driver"}
                className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] px-5 py-2.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                {statusBusy === "new-driver" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Fahrer speichern
              </button>
              <button
                onClick={() => setShowDriverForm(false)}
                className="rounded-xl border border-white/10 px-5 py-2.5 text-xs text-slate-400 hover:border-white/25"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-white/8 bg-[#121826] overflow-hidden">
          <div className="overflow-x-auto">
            {tab === "stats" && stats && (
              <div className="p-5 space-y-6" data-testid="stats-panel">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { label: "Heute", v: stats.today, testid: "stats-card-today" },
                    { label: "Letzte 7 Tage", v: stats.week, testid: "stats-card-week" },
                    { label: "Letzte 30 Tage", v: stats.month, testid: "stats-card-month" },
                    { label: "Gesamt", v: { visitors: null, views: stats.total_views }, testid: "stats-card-total" },
                  ].map((c) => (
                    <div key={c.label} data-testid={c.testid} className="rounded-xl border border-white/8 bg-[#0B0F17]/60 p-4">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-tech">{c.label}</div>
                      <div className="text-2xl font-bold text-white mt-1">{c.v.visitors ?? c.v.views}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {c.v.visitors != null ? `Besucher · ${c.v.views} Aufrufe` : "Seitenaufrufe gesamt"}
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-tech mb-3">Aufrufe · letzte 14 Tage</div>
                  <div data-testid="stats-chart" className="flex items-end gap-1.5 h-32">
                    {(stats.daily || []).map((d) => {
                      const maxV = Math.max(...(stats.daily || []).map((x) => x.views), 1);
                      return (
                        <div key={d.day} className="flex-1 flex flex-col items-center justify-end gap-1 h-full" title={`${d.day}: ${d.views} Aufrufe · ${d.visitors} Besucher`}>
                          <div className="text-[8px] font-mono-tech text-slate-500">{d.views || ""}</div>
                          <div className="w-full rounded-t bg-gradient-to-t from-[#1E6FD9] to-[#29B6F6]" style={{ height: `${Math.max(2, (d.views / maxV) * 100)}%` }} />
                          <div className="text-[8px] font-mono-tech text-slate-600">{d.day.slice(8)}.</div>
                        </div>
                      );
                    })}
                    {(!stats.daily || stats.daily.length === 0) && (
                      <div className="text-slate-500 text-sm py-10 w-full text-center">Noch keine Daten — die Statistik startet mit den nächsten Besuchern.</div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-tech mb-3">Top-Seiten · letzte 30 Tage</div>
                  <table data-testid="stats-top-pages" className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/8">
                        <th className="py-2">Seite</th>
                        <th className="py-2 text-right">Aufrufe</th>
                        <th className="py-2 text-right">Besucher</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(stats.top_pages || []).map((p) => (
                        <tr key={p.path} className="border-b border-white/5">
                          <td className="py-2.5 font-mono-tech text-xs text-slate-300">{p.path}</td>
                          <td className="py-2.5 text-right font-mono-tech text-xs text-slate-200">{p.views}</td>
                          <td className="py-2.5 text-right font-mono-tech text-xs text-cyan-300">{p.visitors}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[10px] text-slate-600">Anonyme Statistik: keine Cookies, keine IP-Speicherung, Bots werden nicht gezählt.</p>
              </div>
            )}

            {tab === "transports" && (
              <table className="w-full text-sm min-w-[1230px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/8">
                    <th className="px-5 py-3.5">Datum</th>
                    <th className="px-5 py-3.5">Kunde</th>
                    <th className="px-5 py-3.5">Route</th>
                    <th className="px-5 py-3.5">Fahrzeug / Ladung</th>
                    <th className="px-5 py-3.5">Distanz</th>
                    <th className="px-5 py-3.5">Preis</th>
                    <th className="px-5 py-3.5">Status</th>
                    <th className="px-5 py-3.5">Fahrer</th>
                    <th className="px-5 py-3.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {transports.length === 0 && (
                    <tr><td colSpan={9} className="px-5 py-10 text-center text-slate-500">Noch keine Transporte.</td></tr>
                  )}
                  {transports.length > 0 && filteredTransports.length === 0 && (
                    <tr><td colSpan={9} className="px-5 py-10 text-center text-slate-500">Keine Treffer für „{search}".</td></tr>
                  )}
                  {filteredTransports.map((t) => (
                    <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-5 py-3.5 font-mono-tech text-xs text-slate-400">
                        {fmtDate(t.created_at)}
                        {t.order_no && <div className="text-[10px] text-cyan-400/90 mt-0.5 font-semibold">Tour-Nr. {t.order_no}</div>}
                        {t.invoice_number && <div className="text-[10px] text-slate-600 mt-0.5">{t.invoice_number}</div>}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="font-semibold text-slate-200">{t.name}</div>
                        <div className="font-mono-tech text-xs text-slate-500">{t.phone}</div>
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-300 max-w-[220px]">
                        {t.pickup} <span className="text-cyan-400">→</span> {t.dropoff}
                        {t.scheduled_at && <div className="text-slate-500 mt-0.5">Termin: {fmtDate(t.scheduled_at)}</div>}
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-300">
                        {VEHICLE_NAMES[t.vehicle] || t.vehicle}
                        <div className="text-slate-500 mt-0.5">
                          {CARGO_NAMES[t.cargo_type] || t.cargo_type}
                          {t.pallets > 0 && ` · ${t.pallets} Paletten`}
                          {!t.pallets && t.pieces > 0 && ` · ${t.pieces} Stück`}
                          {t.dims ? ` · ${t.dims}` : ""}
                          {t.weight_kg ? ` · ${t.weight_kg} kg` : ""}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 font-mono-tech text-xs text-slate-300">{t.distance_km} km</td>
                      <td className="px-5 py-3.5 font-mono-tech text-xs">
                        {t.is_free ? (
                          <Badge tone="green">GRATIS</Badge>
                        ) : (
                          <span className="text-cyan-300 font-bold">{formatEUR(t.price_eur)}</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <StatusPicker
                          testid={`transport-status-${t.id}`}
                          current={t.status}
                          options={TRANSPORT_STATUSES}
                          busy={statusBusy === t.id}
                          onChange={(s) => changeStatus("transports", t.id, s)}
                        />
                      </td>
                      <td className="px-5 py-3.5">
                        <select
                          data-testid={`transport-driver-${t.id}`}
                          value={t.driver_id || ""}
                          disabled={statusBusy === t.id}
                          onChange={(e) => assignDriver(t.id, e.target.value)}
                          className={`${inputCls} min-w-[140px]`}
                        >
                          <option value="">— kein Fahrer —</option>
                          {drivers.map((d) => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-3.5">
                        <div className="flex items-center gap-1">
                          {t.phone && (
                            <a
                              data-testid={`transport-whatsapp-customer-${t.id}`}
                              href={`https://wa.me/${waPhone(t.phone)}?text=${encodeURIComponent(waCustomerMsg(t))}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded-lg text-slate-600 hover:text-emerald-400 hover:bg-emerald-400/10 transition-colors"
                              title="Status per WhatsApp an Kunden"
                            >
                              <WhatsAppIcon size={15} />
                            </a>
                          )}
                          {t.driver_id && drivers.find((d) => d.id === t.driver_id)?.phone && (
                            <a
                              data-testid={`transport-whatsapp-driver-${t.id}`}
                              href={`https://wa.me/${waPhone(drivers.find((d) => d.id === t.driver_id).phone)}?text=${encodeURIComponent(waDriverMsg(t, drivers.find((d) => d.id === t.driver_id)))}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded-lg text-emerald-600/70 border border-emerald-400/20 hover:text-emerald-300 hover:bg-emerald-400/10 transition-colors"
                              title="Tour per WhatsApp an Fahrer"
                            >
                              <WhatsAppIcon size={15} />
                            </a>
                          )}
                          {t.has_signature && (
                            <a
                              data-testid={`transport-signature-${t.id}`}
                              href={`${API}/admin/transports/${t.id}/signature`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded-lg text-slate-600 hover:text-cyan-300 hover:bg-cyan-400/10 transition-colors"
                              title="Unterschrift Empfänger anzeigen"
                            >
                              <PenLine size={15} />
                            </a>
                          )}
                          {t.status === "abgeschlossen" && (
                            <a
                              data-testid={`transport-invoice-${t.id}`}
                              href={`${API}/admin/transports/${t.id}/invoice.pdf`}
                              download
                              className="p-2 rounded-lg text-slate-600 hover:text-cyan-300 hover:bg-cyan-400/10 transition-colors"
                              title="Rechnung als PDF"
                            >
                              <FileText size={15} />
                            </a>
                          )}
                          <button
                            data-testid={`transport-delete-${t.id}`}
                            onClick={() => deleteEntry("transports", t.id)}
                            disabled={statusBusy === t.id}
                            className="p-2 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-400/10 transition-colors disabled:opacity-50"
                            title="Löschen"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {tab === "fahrer" && (
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/8">
                    <th className="px-5 py-3.5">Name</th>
                    <th className="px-5 py-3.5">Telefon</th>
                    <th className="px-5 py-3.5">E-Mail</th>
                    <th className="px-5 py-3.5">Fahrzeug</th>
                    <th className="px-5 py-3.5">Touren</th>
                    <th className="px-5 py-3.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">Noch keine Fahrer angelegt.</td></tr>
                  )}
                  {drivers.map((d) => (
                    <tr key={d.id} data-testid={`driver-row-${d.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-5 py-3.5 font-semibold text-slate-200">{d.name}</td>
                      <td className="px-5 py-3.5 font-mono-tech text-xs text-slate-300">{d.phone}</td>
                      <td className="px-5 py-3.5 text-xs text-slate-400">{d.email || "—"}</td>
                      <td className="px-5 py-3.5 text-xs text-slate-300">{VEHICLE_NAMES[d.vehicle] || d.vehicle || "—"}</td>
                      <td className="px-5 py-3.5 font-mono-tech text-xs text-cyan-300">
                        {transports.filter((t) => t.driver_id === d.id).length}
                      </td>
                      <td className="px-3 py-3.5">
                        <div className="flex items-center gap-1">
                          <button
                            data-testid={`driver-portal-copy-${d.id}`}
                            onClick={() => copyPortalLink(d)}
                            className="p-2 rounded-lg text-slate-600 hover:text-cyan-300 hover:bg-cyan-400/10 transition-colors"
                            title="Portal-Link kopieren"
                          >
                            <Link2 size={15} />
                          </button>
                          {d.phone && (
                            <button
                              data-testid={`driver-portal-whatsapp-${d.id}`}
                              onClick={() => sendPortalWhatsApp(d)}
                              className="p-2 rounded-lg text-slate-600 hover:text-emerald-400 hover:bg-emerald-400/10 transition-colors"
                              title="Portal-Link per WhatsApp senden"
                            >
                              <WhatsAppIcon size={15} />
                            </button>
                          )}
                          <button
                            data-testid={`driver-delete-${d.id}`}
                            onClick={() => deleteEntry("drivers", d.id)}
                            disabled={statusBusy === d.id}
                            className="p-2 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-400/10 transition-colors disabled:opacity-50"
                            title="Löschen"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {tab === "appointments" && (
              <table className="w-full text-sm min-w-[880px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/8">
                    <th className="px-5 py-3.5">Eingegangen</th>
                    <th className="px-5 py-3.5">Kunde</th>
                    <th className="px-5 py-3.5">Service</th>
                    <th className="px-5 py-3.5">Termin</th>
                    <th className="px-5 py-3.5">Status</th>
                    <th className="px-5 py-3.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">Noch keine Termine.</td></tr>
                  )}
                  {appointments.length > 0 && filteredAppointments.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">Keine Treffer für „{search}".</td></tr>
                  )}
                  {filteredAppointments.map((a) => (
                    <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-5 py-3.5 font-mono-tech text-xs text-slate-400">
                        {fmtDate(a.created_at)}
                        {a.appt_no && <div className="text-[10px] text-cyan-400/90 mt-0.5 font-semibold">Termin-Nr. {a.appt_no}</div>}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="font-semibold text-slate-200">{a.name}</div>
                        <div className="font-mono-tech text-xs text-slate-500">{a.phone}</div>
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-300">
                        {a.service}
                        {a.tire_size && <div className="text-slate-500 mt-0.5 font-mono-tech">Reifengröße: {a.tire_size}</div>}
                      </td>
                      <td className="px-5 py-3.5 font-mono-tech text-xs text-cyan-300">{a.date} · {a.time} Uhr</td>
                      <td className="px-5 py-3.5">
                        <StatusPicker
                          testid={`appointment-status-${a.id}`}
                          current={a.status}
                          options={APPOINTMENT_STATUSES}
                          busy={statusBusy === a.id}
                          onChange={(s) => changeStatus("appointments", a.id, s)}
                        />
                      </td>
                      <td className="px-3 py-3.5">
                        <button
                          data-testid={`appointment-delete-${a.id}`}
                          onClick={() => deleteEntry("appointments", a.id)}
                          disabled={statusBusy === a.id}
                          className="p-2 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-400/10 transition-colors disabled:opacity-50"
                          title="Löschen"
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {tab === "orders" && (
              <table className="w-full text-sm min-w-[780px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/8">
                    <th className="px-5 py-3.5">Datum</th>
                    <th className="px-5 py-3.5">Produkte</th>
                    <th className="px-5 py-3.5">Betrag</th>
                    <th className="px-5 py-3.5">Zahlung</th>
                    <th className="px-5 py-3.5">Session</th>
                    <th className="px-5 py-3.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">Noch keine Bestellungen.</td></tr>
                  )}
                  {orders.length > 0 && filteredOrders.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">Keine Treffer für „{search}".</td></tr>
                  )}
                  {filteredOrders.map((o) => (
                    <tr key={o.session_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-5 py-3.5 font-mono-tech text-xs text-slate-400">
                        {fmtDate(o.created_at)}
                        {o.order_ref && <div className="text-[10px] text-cyan-400/90 mt-0.5 font-semibold">Bestell-Nr. {o.order_ref}</div>}
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-300">{(o.items || o.cars || []).join(", ")}</td>
                      <td className="px-5 py-3.5 font-mono-tech text-xs text-cyan-300 font-bold">
                        {formatEUR((o.amount || 0) / 100)}
                      </td>
                      <td className="px-5 py-3.5">
                        <Badge tone={o.payment_status === "paid" ? "green" : o.payment_status === "pending" ? "amber" : "gray"}>
                          {o.payment_status}
                        </Badge>
                      </td>
                      <td className="px-5 py-3.5 font-mono-tech text-[10px] text-slate-600">{o.session_id?.slice(0, 22)}…</td>
                      <td className="px-3 py-3.5">
                        <button
                          data-testid={`order-delete-${o.session_id}`}
                          onClick={() => deleteEntry("orders", o.session_id)}
                          disabled={statusBusy === o.session_id}
                          className="p-2 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-400/10 transition-colors disabled:opacity-50"
                          title="Löschen"
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {tab === "gutscheine" && (
              <table className="w-full text-sm min-w-[780px]" data-testid="admin-vouchers-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/8">
                    <th className="px-5 py-3.5">Code</th>
                    <th className="px-5 py-3.5">Produkt</th>
                    <th className="px-5 py-3.5">Wert</th>
                    <th className="px-5 py-3.5">Käufer</th>
                    <th className="px-5 py-3.5">Datum</th>
                    <th className="px-5 py-3.5">Status</th>
                    <th className="px-5 py-3.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {vouchers.length === 0 && (
                    <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">Noch keine Gutscheine verkauft.</td></tr>
                  )}
                  {vouchers.map((v) => (
                    <tr key={v.code} data-testid={`voucher-row-${v.code}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-5 py-3.5 font-mono-tech text-sm font-bold text-cyan-300">{v.code}</td>
                      <td className="px-5 py-3.5 text-xs text-slate-300">{v.product_name}</td>
                      <td className="px-5 py-3.5 font-mono-tech text-xs text-slate-200">{formatEUR(v.value_eur)}</td>
                      <td className="px-5 py-3.5 text-xs text-slate-400">{v.buyer_email || "—"}</td>
                      <td className="px-5 py-3.5 font-mono-tech text-xs text-slate-400">{fmtDate(v.created_at)}</td>
                      <td className="px-5 py-3.5">
                        <Badge tone={v.redeemed ? "gray" : "green"}>{v.redeemed ? "eingelöst" : "gültig"}</Badge>
                      </td>
                      <td className="px-3 py-3.5">
                        {!v.redeemed && (
                          <button
                            data-testid={`voucher-redeem-${v.code}`}
                            onClick={() => redeemVoucher(v.code)}
                            disabled={statusBusy === v.code}
                            className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-1.5 text-xs font-semibold text-emerald-300 hover:bg-emerald-400/20 transition-colors disabled:opacity-50"
                          >
                            Einlösen
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {tab === "products" && (
              <table className="w-full text-sm min-w-[880px]" data-testid="admin-products-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/8">
                    <th className="px-5 py-3.5">Produkt</th>
                    <th className="px-5 py-3.5">Kategorie</th>
                    <th className="px-5 py-3.5">Preis (€)</th>
                    <th className="px-5 py-3.5">Lager</th>
                    <th className="px-5 py-3.5">Status</th>
                    <th className="px-5 py-3.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {products.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">Noch keine Produkte.</td></tr>
                  )}
                  {products.map((p) => {
                    const edits = rowEdits[p.id] || {};
                    const stock = p.stock ?? 0;
                    return (
                      <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <img src={p.image} alt="" className="w-12 h-9 object-cover rounded-lg" />
                            <div className="font-semibold text-slate-200 text-xs max-w-[220px]">{p.name}</div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-xs text-slate-400">{p.category}</td>
                        <td className="px-5 py-3.5">
                          <input
                            data-testid={`product-price-input-${p.id}`}
                            type="number"
                            step="0.01"
                            min="0"
                            value={edits.price_eur ?? String(p.price_eur)}
                            onChange={(e) => setRowEdits((prev) => ({ ...prev, [p.id]: { ...prev[p.id], price_eur: e.target.value } }))}
                            className={`${inputCls} w-24 font-mono-tech`}
                          />
                        </td>
                        <td className="px-5 py-3.5">
                          <input
                            data-testid={`product-stock-input-${p.id}`}
                            type="number"
                            min="0"
                            value={edits.stock ?? String(stock)}
                            onChange={(e) => setRowEdits((prev) => ({ ...prev, [p.id]: { ...prev[p.id], stock: e.target.value } }))}
                            className={`${inputCls} w-20 font-mono-tech`}
                          />
                        </td>
                        <td className="px-5 py-3.5">
                          {stock <= 0 ? <Badge tone="red">Ausverkauft</Badge> : stock <= 3 ? <Badge tone="amber">Nur noch {stock}</Badge> : <Badge tone="green">Auf Lager</Badge>}
                        </td>
                        <td className="px-3 py-3.5">
                          <div className="flex items-center gap-1">
                            <button
                              data-testid={`product-save-${p.id}`}
                              onClick={() => saveProduct(p)}
                              disabled={statusBusy === p.id}
                              className="p-2 rounded-lg text-slate-600 hover:text-emerald-300 hover:bg-emerald-400/10 transition-colors disabled:opacity-50"
                              title="Speichern"
                            >
                              {statusBusy === p.id ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                            </button>
                            <button
                              data-testid={`product-delete-${p.id}`}
                              onClick={() => deleteEntry("products", p.id)}
                              disabled={statusBusy === p.id}
                              className="p-2 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-400/10 transition-colors disabled:opacity-50"
                              title="Löschen"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {tab === "reports" && (
              <table className="w-full text-sm min-w-[720px]" data-testid="admin-reports-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/8">
                    <th className="px-5 py-3.5">Monat</th>
                    <th className="px-5 py-3.5">Transporte</th>
                    <th className="px-5 py-3.5">Kilometer</th>
                    <th className="px-5 py-3.5">Umsatz Transporte</th>
                    <th className="px-5 py-3.5">Gratisfahrten</th>
                    <th className="px-5 py-3.5">Termine</th>
                  </tr>
                </thead>
                <tbody>
                  {report.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">Noch keine Daten.</td></tr>
                  )}
                  {report.map((m) => (
                    <tr key={m.month} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-5 py-3.5 font-semibold text-slate-200 capitalize">{fmtMonth(m.month)}</td>
                      <td className="px-5 py-3.5 font-mono-tech text-xs text-slate-300">{m.transports}</td>
                      <td className="px-5 py-3.5 font-mono-tech text-xs text-slate-300">{m.km.toLocaleString("de-DE")} km</td>
                      <td className="px-5 py-3.5 font-mono-tech text-xs text-cyan-300 font-bold">{formatEUR(m.revenue)}</td>
                      <td className="px-5 py-3.5">
                        {m.free > 0 ? <Badge tone="green">{m.free}× GRATIS</Badge> : <span className="text-slate-600 text-xs">—</span>}
                      </td>
                      <td className="px-5 py-3.5 font-mono-tech text-xs text-slate-300">{m.appointments}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
